import type { Address } from "../lib/types";
import { useLang } from "../lib/useLang";

export const GOVERNORATES: { en: string; ar: string }[] = [
  { en: "Capital", ar: "العاصمة" },
  { en: "Hawalli", ar: "حولي" },
  { en: "Farwaniya", ar: "الفروانية" },
  { en: "Mubarak Al-Kabeer", ar: "مبارك الكبير" },
  { en: "Ahmadi", ar: "الأحمدي" },
  { en: "Jahra", ar: "الجهراء" },
];

/** Kuwait civil-address fields. Controlled: parent owns the Address value. */
export function AddressFields({
  value,
  onChange,
  areaOptions,
}: {
  value: Address;
  onChange: (patch: Partial<Address>) => void;
  areaOptions?: string[];
}) {
  const { t, pick } = useLang();
  const set = <K extends keyof Address>(k: K, v: string) => onChange({ [k]: v });

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t("checkout.governorate")}>
          <select
            className="input"
            value={value.governorate}
            onChange={(e) => set("governorate", e.target.value)}
          >
            <option value="">—</option>
            {GOVERNORATES.map((g) => (
              <option key={g.en} value={g.en}>
                {pick(g.en, g.ar)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("checkout.area")} required>
          <input
            className="input"
            list="areas"
            value={value.area}
            onChange={(e) => set("area", e.target.value)}
          />
          {areaOptions && (
            <datalist id="areas">
              {areaOptions.map((a) => (
                <option key={a} value={a} />
              ))}
            </datalist>
          )}
        </Field>
        <Field label={t("checkout.block")} required>
          <input className="input" value={value.block} onChange={(e) => set("block", e.target.value)} />
        </Field>
        <Field label={t("checkout.street")} required>
          <input className="input" value={value.street} onChange={(e) => set("street", e.target.value)} />
        </Field>
        <Field label={t("checkout.building")} required>
          <input className="input" value={value.building} onChange={(e) => set("building", e.target.value)} />
        </Field>
        <Field label={t("checkout.jadda")}>
          <input className="input" value={value.jadda ?? ""} onChange={(e) => set("jadda", e.target.value)} />
        </Field>
        <Field label={t("checkout.floor")}>
          <input className="input" value={value.floor ?? ""} onChange={(e) => set("floor", e.target.value)} />
        </Field>
        <Field label={t("checkout.apartment")}>
          <input
            className="input"
            value={value.apartment ?? ""}
            onChange={(e) => set("apartment", e.target.value)}
          />
        </Field>
        <Field label={t("checkout.paci")}>
          <input
            className="input"
            inputMode="numeric"
            value={value.paci ?? ""}
            onChange={(e) => set("paci", e.target.value)}
          />
        </Field>
      </div>
      <Field label={t("checkout.notes")}>
        <textarea
          className="input"
          rows={2}
          value={value.notes ?? ""}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Field>
    </>
  );
}

export function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-black/55">
        {label} {required && <span className="text-brand">*</span>}
      </span>
      {children}
    </label>
  );
}

/** One-line human summary of an address, for pickers / lists. */
export function addressSummary(a: Address): string {
  return [a.area, a.block && `Blk ${a.block}`, a.street, a.building]
    .filter(Boolean)
    .join(", ");
}
